import { formatIsoInstant, parseIsoInstant, truncateToSecond } from './timeGrid'

// TimelineQuery (03-dominio.md): the query of the screen's reading, as a plain object with pure functions.
//   { view: 'device' | 'worker', deviceId, workerId, from, to, asOf, page, held }
// `from`, `to` and `asOf` are epoch milliseconds, `to` truncated to the second. `deviceId` / `workerId` are a number or ''
// when there is none: by machine, '' as worker means "all workers" (RN9, GATE2-G12) and '' as machine means "nothing
// chosen"; by worker it is the other way round. `asOf` is the "now" the range was built with: "next" is only available
// while `to` is behind it (RN11). `held` stops the query from being asked by itself: the screen emptied a filter because
// Sun said it does not exist (404), and asking again would not be what the user chose (RN19).

const HOUR = 3600 * 1000
export const DEFAULT_RANGE_MS = 72 * HOUR
export const MIN_RANGE_MS = HOUR

const nowSecond = (nowMs) => truncateToSecond(nowMs).getTime()

/** Estado inicial (RN9): by machine, nothing chosen, the last 72 h ending now. */
export const initialQuery = (nowMs) => {
  const to = nowSecond(nowMs)
  return { view: 'device', deviceId: '', workerId: '', from: to - DEFAULT_RANGE_MS, to, asOf: to, page: 0, held: false }
}

/** Whether there is something to ask (RN9, RN13): a machine by machine, a worker by worker. */
export const hasTarget = (query) => (query.view === 'device' ? query.deviceId !== '' : query.workerId !== '')

/** Whether the screen asks it: it has a target and it was not held after a 404. */
export const canQuery = (query) => hasTarget(query) && !query.held

/** The parameters of `apiGet('dataCorrection/timeline', …)` (RN10, RN82): empty optionals are never sent. */
export const toParams = (query) => {
  const params = {}
  if (query.deviceId !== '') params.deviceId = query.deviceId
  if (query.workerId !== '') params.workerId = query.workerId
  params.from = formatIsoInstant(query.from)
  params.to = formatIsoInstant(query.to)
  if (query.view === 'worker' && query.deviceId === '') params.page = query.page
  return params
}

/** «‹» moves the range back its own length (RN11). */
export const previousPeriod = (query) => {
  const length = query.to - query.from
  return { ...query, from: query.from - length, to: query.from, page: 0, held: false }
}

/** «›» moves forward its own length; if that would pass now, the range ends at now (RN11). */
export const nextPeriod = (query, nowMs) => {
  const length = query.to - query.from
  const now = nowSecond(nowMs)
  const to = query.to + length
  if (to >= now) return { ...query, from: now - length, to: now, asOf: now, page: 0, held: false }
  return { ...query, from: query.to, to, page: 0, held: false }
}

/** «›» is disabled while the range already ends at the "now" it was asked with. */
export const canGoNext = (query) => query.to < query.asOf

/** TOO_MANY_ROWS (RN17): half of the range keeping `to`, never below one hour (GATE2-G11). */
export const halved = (query) => {
  const half = Math.max(MIN_RANGE_MS, (query.to - query.from) / 2)
  return { ...query, from: query.to - half, held: false }
}

/** Another machine or worker: back to 72 h ending now, page 0 (RN11, RN14). */
export const withFilters = (query, filters, nowMs) => {
  const to = nowSecond(nowMs)
  return { ...query, ...filters, from: to - DEFAULT_RANGE_MS, to, asOf: to, page: 0, held: false }
}

/** Grouping (RN13): both filters are kept; the page goes back to 0. */
export const withView = (query, view) => ({ ...query, view, page: 0, held: false })

export const withPage = (query, page) => ({ ...query, page, held: false })

/** The filter `field` ('deviceId' or 'workerId') does not exist for Sun: empty it and do not ask again by itself (RN19). */
export const withFilterEmptied = (query, field) => ({ ...query, [field]: '', held: true })

const SHOW_MARGIN_MS = HOUR
export const MAX_RANGE_MS = 31 * 24 * HOUR

/**
 * Story 211 (RN16, D-S7): «View on the timeline» of a correction. By machine, with its machine and its worker, and a period
 * that shows the corrected stretch `window` ({start, end} ISO): the stretch with 1 h at each side; shorter than 72 h it is
 * lengthened at its end up to 72 h (the default length); at most 31 days (Sun's limit), then it starts 1 h before the stretch;
 * and it never passes now: then it ends now and keeps its length. Page 0, and `asOf` now.
 */
export const showCorrection = (query, { deviceId, workerId, window }, nowMs) => {
  const now = nowSecond(nowMs)
  const startMs = parseIsoInstant(window.start).getTime() - SHOW_MARGIN_MS
  const endMs = parseIsoInstant(window.end).getTime() + SHOW_MARGIN_MS
  let from = startMs
  let to = Math.max(endMs, startMs + DEFAULT_RANGE_MS)
  if (to - from > MAX_RANGE_MS) to = from + MAX_RANGE_MS
  if (to > now) {
    const length = to - from
    to = now
    from = now - length
  }
  return {
    ...query,
    view: 'device',
    deviceId,
    workerId: workerId || '',
    from: truncateToSecond(from).getTime(),
    to: truncateToSecond(to).getTime(),
    asOf: now,
    page: 0,
    held: false
  }
}
